import React, { FC } from "react"
import {  Form, Table, message } from "antd"
import type { ColumnsType } from 'antd/es/table'
import { useAntdTable, useTextSelection } from "ahooks"
import FilterBar from "./FilterBar"
import classnames from 'classnames'
import './index.scss'

interface QueryTableProps {
  rowSelection?: any
  columns: ColumnsType<any>
  remoteUrl: string
  filterBarOptions?: any[]
  onRowClick?: (record: any, event: React.MouseEvent<HTMLTableRowElement>) => void
  antdTableProps?: any
}

interface Result {
  list: any[]
  total: number,
}

const QueryTable: FC<QueryTableProps> = ({
  rowSelection,
  columns,
  remoteUrl,
  filterBarOptions,
  onRowClick,
  antdTableProps
}) => {
  const [form] = Form.useForm()
  const rowKey = antdTableProps?.rowKey ? antdTableProps.rowKey : 'keyIndex'
  const [activeRowKey, setActiveRowKey] = React.useState<React.Key>()

  const getTableData = (
    { current, pageSize },
    formData: Object,
  ): Promise<Result> => {
    let query = `page=${current}&size=${pageSize}`
    Object.entries(formData).forEach(([key, value]) => {
      if (value) {
        query += `&${key}=${value}`;
      }
    })
    return fetch(`${remoteUrl}?${query}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
      .then((res) => res.json())
      .then((res) => {
        if (res.code === 0) {
          return {
            total: res.total,
            list: res.data.items,
          }
        } else {
          res.message && message.error(res.message)
        }
      })
  }

  const { tableProps, search } = useAntdTable(getTableData, {
    defaultPageSize: 20,
    form,
  })

  const { submit, reset } = search

  const rowClassName = React.useCallback(
    (record: Record<string, any>, index: number, indent: number) => {
      const propsClassName =
        typeof tableProps?.rowClassName === 'function'
          ? tableProps?.rowClassName(record, index, indent)
          : tableProps?.rowClassName

      if (onRowClick) {
        const _rowKey = typeof rowKey === 'function' ? rowKey(record) : record[rowKey]

        return classnames({
          [propsClassName!]: !!propsClassName,
          ['query-table-click-row']: true,
          ['query-table-active-row']: activeRowKey === _rowKey,
        })
      } else {
        return propsClassName!
      }
    },
    [tableProps?.rowClassName, onRowClick, activeRowKey]
  )

  return (
    <>
      <FilterBar
        filterBarOptions={filterBarOptions}
        submit={submit}
        reset={reset}
        form={form}
        showSearchButton={false}
        autoSearch={true}
      />
      <div className="query_table_list">
        <Table
          rowSelection={rowSelection}
          columns={columns}
          locale={{
            emptyText: '暂无数据'
          }}
          rowKey="id"
          className="whitespace-nowrap overflow-x-auto text-ellipsis"
          onRow={(record) => {
            return {
              onClick: (e) => {
                e.stopPropagation()
                const selection = window.getSelection()!.toString()
                if (selection.length <= 0) {
                  onRowClick?.(record, e)
                  setActiveRowKey(typeof rowKey === 'function' ? rowKey(record) : record[rowKey])
                }
              },
            };
          }}
          {...antdTableProps}
          {...tableProps}
          rowClassName={rowClassName}
          scroll={{
            x: 'max-content',
            scrollToFirstRowOnChange: true,
            ...tableProps?.scroll,
          }}
        />
      </div>
    </>
  )
}

export default QueryTable
